import React, { useEffect, useState } from "react";
import HostNav from "../components/hostComponents/HostNav";
import { useDispatch, useSelector } from "react-redux";
import {
  getFlags,
  getHostProperties,
  getHostEarnings,
  getAllOrders,
} from "../store/thunks/hostActions";
import { useNavigate, Link } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import {
  ArrowRight,
  Building2,
  DollarSign,
  ShoppingBag,
  TrendingUp,
  Plus,
  Settings,
  Calendar,
  Clock,
  AlertTriangle,
  Eye,
  MousePointerClick,
  Timer,
  ConciergeBell,
  Info,
  ArrowUpRight,
  X,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  updatePropertyReset,
  deletePropertyReset,
  addPropertyReset,
} from "../store/slices/hostSlices";
import request from "../utils/request";
import TutorialModal from "../components/TutorialModal";
import { isUpcomingOrder } from "../utils/orderStatus";
// import ConciergePromoCard from "../components/hostComponents/ConciergePromoCard";

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();
const isStatus = (status, target) =>
  normalizeStatus(status) === normalizeStatus(target);

/* ─── Compact Analytics Strip ─────────────────────────────────────── */
function AnalyticsBar({
  activeProperties,
  totalEarnings,
  totalOrders,
  avgOrderValue,
}) {
  const navigate = useNavigate();

  const stats = [
    {
      label: "Properties",
      value: String(activeProperties),
      icon: <Building2 size={12} />,
      onClick: () => navigate("/properties"),
    },
    {
      label: "Earnings",
      value: `$${totalEarnings.toFixed(2)}`,
      icon: <DollarSign size={12} />,
      accent: totalEarnings > 0,
      onClick: () => navigate("/earnings"),
    },
    {
      label: "Orders",
      value: String(totalOrders),
      icon: <ShoppingBag size={12} />,
      onClick: () => navigate("/order-list"),
    },
    {
      label: "Avg value",
      value: `$${avgOrderValue.toFixed(2)}`,
      icon: <TrendingUp size={12} />,
      onClick: () => navigate("/earnings"),
    },
  ];

  return (
    <div className="grid grid-cols-2 laptop:grid-cols-4 gap-2.5">
      {stats.map((s) => (
        <button
          key={s.label}
          onClick={s.onClick}
          className="group text-left rounded-[14px] bg-white border border-[#EAEAEA] px-4 py-3.5 hover:border-[#111111] transition-colors"
        >
          <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">
            {s.icon}
            {s.label}
          </div>
          <div
            className={`mt-1 text-[20px] font-extrabold leading-tight tabular-nums ${
              s.accent ? "text-[#111111]" : "text-[#111111]"
            }`}
            style={{ letterSpacing: -0.3 }}
          >
            {s.value}
          </div>
        </button>
      ))}
    </div>
  );
}

const ANALYTICS_WINDOWS = [
  { key: "last7", label: "Last 7 Days" },
  { key: "mtd", label: "MTD" },
  { key: "ytd", label: "YTD" },
  { key: "allTime", label: "All Time" },
];

const emptyAnalyticsMetric = {
  pageViews: 0,
  eventCount: 0,
  engagementSeconds: 0,
  serviceRequests: 0,
};

const emptyRevenueMetric = {
  count: 0,
  minPotentialRevenue: 0,
  maxPotentialRevenue: 0,
};

const emptyRevenueOpportunityMonth = {
  period: "2026 YTD",
  startDate: "2026-01-01",
  endDate: null,
  oneNightGap: emptyRevenueMetric,
  twoNightGap: emptyRevenueMetric,
  earlyCheckIn: emptyRevenueMetric,
};

const REVENUE_SIGNAL_TYPES = {
  oneNightGap: "one_night_gap",
  twoNightGap: "two_night_gap",
  earlyCheckIn: "early_check_in",
};

function formatCompactNumber(value) {
  const number = Number(value || 0);
  if (number >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
  if (number >= 1000) return `${(number / 1000).toFixed(1)}K`;
  return String(number);
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDuration(seconds) {
  const value = Number(seconds || 0);
  if (value < 60) return `${Math.round(value)}s`;
  const minutes = Math.round(value / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${hours}h`;
}

function formatShortDate(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function hasRevenueOpportunity(revenueMonth) {
  return [
    revenueMonth?.oneNightGap,
    revenueMonth?.twoNightGap,
    revenueMonth?.earlyCheckIn,
  ].some((metric) => (
    Number(metric?.count || 0) > 0 ||
    Number(metric?.minPotentialRevenue || 0) > 0 ||
    Number(metric?.maxPotentialRevenue || 0) > 0
  ));
}

function isRevenueOpportunityMonth(value) {
  return !!(
    value &&
    (value.oneNightGap || value.twoNightGap || value.earlyCheckIn)
  );
}

function resolveRevenueOpportunityMonth(revenueOpportunities) {
  if (isRevenueOpportunityMonth(revenueOpportunities?.since2026)) {
    return revenueOpportunities.since2026;
  }
  if (isRevenueOpportunityMonth(revenueOpportunities?.may2026)) {
    return revenueOpportunities.may2026;
  }
  if (isRevenueOpportunityMonth(revenueOpportunities)) {
    return revenueOpportunities;
  }
  const firstMonth = Object.values(revenueOpportunities || {}).find(
    isRevenueOpportunityMonth
  );
  return firstMonth || emptyRevenueOpportunityMonth;
}

function AnalyticsMetricCard({ label, value, icon, active }) {
  return (
    <div className="rounded-[14px] bg-white border border-[#EAEAEA] px-4 py-3.5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">
            {label}
          </div>
          <div className="mt-1 text-[22px] font-extrabold leading-tight text-[#111111] tabular-nums">
            {value}
          </div>
        </div>
        <div
          className={`w-9 h-9 rounded-[10px] flex items-center justify-center ${
            active ? "bg-[#EF4949] text-white" : "bg-[#F7F7F8] text-[#5F6368]"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function RevenueOpportunityTable({ revenueMonth, onSelectSignal }) {
  if (!hasRevenueOpportunity(revenueMonth)) return null;

  const rows = [
    {
      key: "oneNightGap",
      signal: "1-night gap nights",
      metric: revenueMonth.oneNightGap || emptyRevenueMetric,
      tooltip: "Low and high estimates use 65% to 85% of the adjacent average nightly rate.",
    },
    {
      key: "twoNightGap",
      signal: "2-night gap nights",
      metric: revenueMonth.twoNightGap || emptyRevenueMetric,
      tooltip: "Low and high estimates use 75% to 90% of the adjacent average nightly rate, multiplied by two open nights.",
    },
    {
      key: "earlyCheckIn",
      signal: "Early check-in",
      metric: revenueMonth.earlyCheckIn || emptyRevenueMetric,
      tooltip: "Low and high estimates use the nightly rate divided by 24 hours, multiplied by a 2 to 6 hour early arrival window.",
    },
  ];
  const totalLow = rows.reduce((sum, row) => sum + Number(row.metric.minPotentialRevenue || 0), 0);
  const totalHigh = rows.reduce((sum, row) => sum + Number(row.metric.maxPotentialRevenue || 0), 0);

  return (
    <div className="rounded-[16px] bg-[#FAFAFA] border border-[#EAEAEA] p-4">
      <div className="flex flex-col laptop:flex-row laptop:items-start laptop:justify-between gap-3 mb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 mb-1">
            <DollarSign size={12} className="text-[#EF4949]" />
            <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
              Revenue opportunities
            </span>
          </div>
          <h3 className="text-[16px] font-extrabold text-[#111111]" style={{ letterSpacing: -0.2 }}>
            2026 guest revenue signals Bellhopt can help capture.
          </h3>
          <p className="text-[11px] text-[#888888] mt-1 max-w-[560px]">
            Subscribe to Bellhopt Concierge for $8/property/month to turn these signals into guest offers.
          </p>
        </div>
        <div className="rounded-[12px] bg-white border border-[#EAEAEA] px-3 py-2 min-w-[180px]">
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">
            Estimate range
          </div>
          <div className="mt-0.5 text-[15px] font-extrabold text-[#111111] tabular-nums">
            {formatCurrency(totalLow)} - {formatCurrency(totalHigh)}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-separate border-spacing-0">
          <thead>
            <tr>
              {["Signal", "Count", "Low estimate", "High estimate"].map((label) => (
                <th
                  key={label}
                  className="text-left text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] border-b border-[#EAEAEA] pb-2"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="group">
                <td className="py-3 pr-4 border-b border-[#EAEAEA]">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-bold text-[#111111]">
                      {row.signal}
                    </span>
                    <span
                      title={row.tooltip}
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white border border-[#EAEAEA] text-[#888888] group-hover:text-[#111111]"
                    >
                      <Info size={11} />
                    </span>
                  </div>
                </td>
                <td className="py-3 pr-4 border-b border-[#EAEAEA] text-[13px] font-extrabold text-[#111111] tabular-nums">
                  {formatCompactNumber(row.metric.count)}
                </td>
                <td className="py-3 pr-4 border-b border-[#EAEAEA] text-[13px] font-extrabold text-[#111111] tabular-nums">
                  {formatCurrency(row.metric.minPotentialRevenue)}
                </td>
                <td className="py-3 border-b border-[#EAEAEA] text-[13px] font-extrabold text-[#EF4949] tabular-nums">
                  <div className="flex items-center justify-between gap-3">
                    <span>{formatCurrency(row.metric.maxPotentialRevenue)}</span>
                    <button
                      type="button"
                      onClick={() => onSelectSignal?.(row)}
                      className="inline-flex items-center gap-1 rounded-[9px] bg-white border border-[#EAEAEA] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#111111] hover:border-[#111111]"
                    >
                      Drill in
                      <ArrowRight size={10} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RevenueOpportunityDrilldownModal({
  open,
  signal,
  rows,
  loading,
  error,
  pagination,
  onClose,
  onPageChange,
}) {
  if (!open) return null;

  const page = Number(pagination?.page || 1);
  const pages = Number(pagination?.pages || 1);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 px-4 py-6">
      <div className="w-full max-w-[980px] max-h-[86vh] overflow-hidden rounded-[20px] bg-white border border-[#EAEAEA] shadow-[0_24px_80px_rgba(0,0,0,0.22)]">
        <div className="flex items-start justify-between gap-4 border-b border-[#EAEAEA] px-5 py-4">
          <div>
            <div className="inline-flex items-center gap-1.5 mb-1">
              <DollarSign size={12} className="text-[#EF4949]" />
              <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
                Opportunity drilldown
              </span>
            </div>
            <h3 className="text-[18px] font-extrabold text-[#111111]">
              {signal?.signal || "Revenue opportunity"}
            </h3>
            <p className="text-[12px] text-[#777777] mt-1">
              Cached 2026 reservation signals by home. No guest PII is shown.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-[10px] bg-[#F7F7F8] text-[#111111] flex items-center justify-center hover:bg-[#EAEAEA]"
            aria-label="Close opportunity drilldown"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 overflow-auto max-h-[calc(86vh-145px)]">
          {loading ? (
            <div className="h-[220px] flex items-center justify-center">
              <CircularProgress size={30} sx={{ color: "#EF4949" }} />
            </div>
          ) : error ? (
            <div className="rounded-[14px] bg-[#FEF2F2] border border-[#FECACA] p-4 text-[13px] font-semibold text-[#991B1B]">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-[14px] bg-[#FAFAFA] border border-[#EAEAEA] p-6 text-center">
              <p className="text-[14px] font-bold text-[#111111]">
                No rows found for this signal.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-separate border-spacing-0">
                <thead>
                  <tr>
                    {["Home", "Date", "Context", "Count", "Low", "High"].map((label) => (
                      <th
                        key={label}
                        className="text-left text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] border-b border-[#EAEAEA] pb-2"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const opportunity = row.opportunity || {};
                    const isGap = opportunity.signalType === "one_night_gap" || opportunity.signalType === "two_night_gap";
                    const context = isGap
                      ? `${formatShortDate(opportunity.previousCheckOutDate)} to ${formatShortDate(opportunity.nextCheckInDate)}`
                      : `${formatShortDate(opportunity.checkInDate)} check-in`;
                    return (
                      <tr key={`${opportunity.signalType}-${opportunity.propertyId}-${opportunity.opportunityDate}-${index}`}>
                        <td className="py-3 pr-4 border-b border-[#EAEAEA]">
                          <div className="text-[13px] font-bold text-[#111111]">
                            {opportunity.propertyLabel || "Property"}
                          </div>
                          <div className="text-[10px] text-[#888888] mt-0.5">
                            {opportunity.source || "reservation"}
                          </div>
                        </td>
                        <td className="py-3 pr-4 border-b border-[#EAEAEA] text-[12px] font-bold text-[#111111] tabular-nums">
                          {formatShortDate(opportunity.opportunityDate)}
                        </td>
                        <td className="py-3 pr-4 border-b border-[#EAEAEA] text-[12px] text-[#5F6368]">
                          {context}
                        </td>
                        <td className="py-3 pr-4 border-b border-[#EAEAEA] text-[12px] font-bold text-[#111111] tabular-nums">
                          {isGap ? `${opportunity.gapNights || 0} night${Number(opportunity.gapNights || 0) === 1 ? "" : "s"}` : "Eligible"}
                        </td>
                        <td className="py-3 pr-4 border-b border-[#EAEAEA] text-[12px] font-extrabold text-[#111111] tabular-nums">
                          {formatCurrency(opportunity.minPotentialRevenue)}
                        </td>
                        <td className="py-3 border-b border-[#EAEAEA] text-[12px] font-extrabold text-[#EF4949] tabular-nums">
                          {formatCurrency(opportunity.maxPotentialRevenue)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[#EAEAEA] px-5 py-3">
          <p className="text-[11px] font-semibold text-[#888888]">
            Page {page} of {pages} · {formatCompactNumber(pagination?.total || 0)} rows
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
              className="rounded-[10px] border border-[#EAEAEA] bg-white px-3 py-2 text-[11px] font-bold text-[#111111] disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => onPageChange(page + 1)}
              className="rounded-[10px] bg-[#111111] px-3 py-2 text-[11px] font-bold text-white disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-[12px] bg-white border border-[#EAEAEA] shadow-lg px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888] mb-1">
        {label}
      </div>
      <div className="space-y-1">
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-5 text-[12px]">
            <span className="font-semibold text-[#5F6368]">{entry.name}</span>
            <span className="font-extrabold text-[#111111] tabular-nums">
              {formatCompactNumber(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function HostAnalyticsPanel({ hostId }) {
  const [analytics, setAnalytics] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState("last7");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [drilldownSignal, setDrilldownSignal] = useState(null);
  const [drilldownPage, setDrilldownPage] = useState(1);
  const [drilldownRows, setDrilldownRows] = useState([]);
  const [drilldownPagination, setDrilldownPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 1,
  });
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState("");

  useEffect(() => {
    if (!hostId) return;

    let cancelled = false;
    const loadAnalytics = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const { data } = await request.get(`/hosts/${hostId}/analytics/summary`);
        if (!cancelled) setAnalytics(data?.analytics || null);
      } catch (err) {
        if (!cancelled) {
          setAnalytics(null);
          setLoadError("Analytics are unavailable right now.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAnalytics();
    return () => {
      cancelled = true;
    };
  }, [hostId]);

  useEffect(() => {
    if (!hostId || !drilldownSignal) return;

    let cancelled = false;
    const loadDrilldown = async () => {
      setDrilldownLoading(true);
      setDrilldownError("");
      try {
        const { data } = await request.get(`/hosts/${hostId}/analytics/opportunities`, {
          params: {
            signalType: REVENUE_SIGNAL_TYPES[drilldownSignal.key],
            page: drilldownPage,
            limit: 25,
          },
        });
        if (cancelled) return;
        setDrilldownRows(Array.isArray(data?.rows) ? data.rows : []);
        setDrilldownPagination(
          data?.pagination || { page: 1, limit: 25, total: 0, pages: 1 }
        );
      } catch (err) {
        if (!cancelled) {
          setDrilldownRows([]);
          setDrilldownPagination({ page: 1, limit: 25, total: 0, pages: 1 });
          setDrilldownError("Unable to load opportunity details right now.");
        }
      } finally {
        if (!cancelled) setDrilldownLoading(false);
      }
    };

    loadDrilldown();
    return () => {
      cancelled = true;
    };
  }, [hostId, drilldownSignal, drilldownPage]);

  const windowMetrics = analytics?.windows?.[selectedWindow] || emptyAnalyticsMetric;
  const topHostMetrics =
    analytics?.benchmark?.[selectedWindow]?.topHost || emptyAnalyticsMetric;
  const revenueMonth = resolveRevenueOpportunityMonth(
    analytics?.revenueOpportunities
  );
  const hasOpportunity = hasRevenueOpportunity(revenueMonth);
  const hasAnyAnalytics =
    Number(windowMetrics.pageViews || 0) > 0 ||
    Number(windowMetrics.eventCount || 0) > 0 ||
    Number(windowMetrics.serviceRequests || 0) > 0 ||
    Number(windowMetrics.engagementSeconds || 0) > 0 ||
    hasOpportunity;

  const comparisonData = [
    {
      label: "Page views",
      you: Number(windowMetrics.pageViews || 0),
      topHost: Number(topHostMetrics.pageViews || 0),
    },
    {
      label: "Events",
      you: Number(windowMetrics.eventCount || 0),
      topHost: Number(topHostMetrics.eventCount || 0),
    },
    {
      label: "Requests",
      you: Number(windowMetrics.serviceRequests || 0),
      topHost: Number(topHostMetrics.serviceRequests || 0),
    },
  ];

  const trendData = (analytics?.series || []).map((item) => ({
    date: String(item.date || "").slice(5),
    pageViews: Number(item.pageViews || 0),
    serviceRequests: Number(item.serviceRequests || 0),
  }));

  const openDrilldown = (signal) => {
    setDrilldownSignal(signal);
    setDrilldownPage(1);
    setDrilldownRows([]);
    setDrilldownPagination({ page: 1, limit: 25, total: 0, pages: 1 });
    setDrilldownError("");
  };

  const closeDrilldown = () => {
    setDrilldownSignal(null);
    setDrilldownPage(1);
    setDrilldownRows([]);
    setDrilldownPagination({ page: 1, limit: 25, total: 0, pages: 1 });
    setDrilldownError("");
  };

  return (
    <section className="rounded-[20px] bg-white border border-[#EAEAEA] p-5 shadow-[0_18px_48px_rgba(17,17,17,0.04)]">
      <div className="flex flex-col laptop:flex-row laptop:items-start laptop:justify-between gap-4 mb-5">
        <div>
          <div className="inline-flex items-center gap-1.5 mb-1">
            <TrendingUp size={12} className="text-[#EF4949]" />
            <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
              Guest analytics
            </span>
          </div>
          <h2 className="text-[18px] font-extrabold text-[#111111]" style={{ letterSpacing: -0.2 }}>
            Guest page activity
          </h2>
          <p className="text-[12px] text-[#777777] mt-1 max-w-[620px]">
            Guest-facing Bellhopt traffic, engagement time, concierge request volume, and missed revenue signals.
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {ANALYTICS_WINDOWS.map((item) => {
            const active = selectedWindow === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setSelectedWindow(item.key)}
                className={`px-3 py-2 rounded-[10px] text-[11px] font-bold transition-all ${
                  active
                    ? "bg-[#111111] text-white shadow-[0_8px_18px_rgba(17,17,17,0.14)]"
                    : "bg-[#F7F7F8] text-[#5F6368] hover:bg-[#EAEAEA]"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="h-[300px] flex items-center justify-center">
          <CircularProgress size={34} sx={{ color: "#EF4949" }} />
        </div>
      ) : loadError ? (
        <div className="rounded-[14px] bg-[#FEF2F2] border border-[#FECACA] p-4 text-[13px] font-semibold text-[#991B1B]">
          {loadError}
        </div>
      ) : !hasAnyAnalytics ? (
        <div className="rounded-[14px] bg-[#FAFAFA] border border-[#EAEAEA] p-6 text-center">
          <p className="text-[14px] font-bold text-[#111111]">
            Analytics will appear after guest traffic is processed.
          </p>
          <p className="text-[12px] text-[#888888] mt-1">
            The cache updates from GA4 and guest service requests on the backend.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {hasOpportunity && (
            <RevenueOpportunityTable
              revenueMonth={revenueMonth}
              onSelectSignal={openDrilldown}
            />
          )}

          <div className="grid grid-cols-2 laptop:grid-cols-4 gap-2.5">
            <AnalyticsMetricCard
              label="Page views"
              value={formatCompactNumber(windowMetrics.pageViews)}
              icon={<Eye size={15} />}
              active
            />
            <AnalyticsMetricCard
              label="Events"
              value={formatCompactNumber(windowMetrics.eventCount)}
              icon={<MousePointerClick size={15} />}
            />
            <AnalyticsMetricCard
              label="Service requests"
              value={formatCompactNumber(windowMetrics.serviceRequests)}
              icon={<ConciergeBell size={15} />}
            />
            <AnalyticsMetricCard
              label="Time spent"
              value={formatDuration(windowMetrics.engagementSeconds)}
              icon={<Timer size={15} />}
            />
          </div>

          <div className="grid grid-cols-1 laptop:grid-cols-2 gap-4">
            <div className="rounded-[16px] bg-[#FAFAFA] border border-[#EAEAEA] p-4 min-h-[280px]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-[13px] font-extrabold text-[#111111]">
                    You vs top host
                  </h3>
                  <p className="text-[10px] text-[#888888] mt-0.5">
                    Anonymous benchmark for this window
                  </p>
                </div>
                <div className="w-8 h-8 rounded-[10px] bg-white border border-[#EAEAEA] flex items-center justify-center text-[#EF4949]">
                  <ArrowUpRight size={14} />
                </div>
              </div>
              <ResponsiveContainer width="100%" height={225}>
                <BarChart data={comparisonData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#EAEAEA" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#777777" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#777777" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<AnalyticsTooltip />} cursor={{ fill: "rgba(239,73,73,0.06)" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="you" name="You" fill="#EF4949" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="topHost" name="Top host" fill="#111111" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-[16px] bg-[#FAFAFA] border border-[#EAEAEA] p-4 min-h-[280px]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-[13px] font-extrabold text-[#111111]">
                    Recent trend
                  </h3>
                  <p className="text-[10px] text-[#888888] mt-0.5">
                    Daily page views and guest requests
                  </p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#888888]">
                  30 days
                </span>
              </div>
              <ResponsiveContainer width="100%" height={225}>
                <LineChart data={trendData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#EAEAEA" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#777777" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#777777" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<AnalyticsTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="pageViews" name="Page views" stroke="#EF4949" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="serviceRequests" name="Requests" stroke="#111111" strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5, stroke: "#111111", strokeWidth: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
      <RevenueOpportunityDrilldownModal
        open={!!drilldownSignal}
        signal={drilldownSignal}
        rows={drilldownRows}
        loading={drilldownLoading}
        error={drilldownError}
        pagination={drilldownPagination}
        onClose={closeDrilldown}
        onPageChange={setDrilldownPage}
      />
    </section>
  );
}

/* ─── Upcoming Orders ─────────────────────────────────────────────── */
function UpcomingOrdersWidget({ orders = [], receiveEntryInstructionsEmails = false }) {
  const navigate = useNavigate();
  const safeOrders = Array.isArray(orders) ? orders : [];

  const upcomingOrders = safeOrders
    .filter((order) => isUpcomingOrder(order))
    .sort((a, b) => new Date(a.inStockDate) - new Date(b.inStockDate));

  const ordersNeedingDetails = receiveEntryInstructionsEmails
    ? upcomingOrders.filter((order) => !order.hostOrderDetailsInputted).length
    : 0;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="rounded-[18px] bg-white border border-[#EAEAEA] p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 mb-1">
            <Calendar size={12} className="text-[#EF4949]" />
            <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
              Upcoming
            </span>
          </div>
          <h3
            className="text-[16px] font-extrabold text-[#111111]"
            style={{ letterSpacing: -0.2 }}
          >
            Orders
            <span className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-[#F7F7F8] text-[10px] font-bold text-[#5F6368] tabular-nums">
              {upcomingOrders.length}
            </span>
          </h3>
        </div>
        <Link
          to="/order-list"
          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#111111] hover:text-[#EF4949] transition-colors"
        >
          View all
          <ArrowRight size={11} strokeWidth={2.5} />
        </Link>
      </div>

      {receiveEntryInstructionsEmails && ordersNeedingDetails > 0 && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-[10px] bg-[#FEF2F2] border border-[#FECACA]">
          <AlertTriangle size={12} className="text-[#EF4949] flex-shrink-0" />
          <p className="text-[11px] font-semibold text-[#991B1B]">
            <span className="font-extrabold tabular-nums">{ordersNeedingDetails}</span>{" "}
            order{ordersNeedingDetails > 1 ? "s" : ""} need entry instructions
          </p>
        </div>
      )}

      {upcomingOrders.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
          <div className="w-11 h-11 mb-2.5 rounded-[12px] bg-[#F7F7F8] border border-[#EAEAEA] flex items-center justify-center">
            <Clock size={18} className="text-[#888888]" />
          </div>
          <p className="text-[13px] font-bold text-[#111111]">No upcoming orders</p>
          <p className="text-[11px] text-[#888888] mt-0.5">
            New bookings will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-1.5 flex-1 overflow-y-auto min-h-0">
          {upcomingOrders.map((order, index) => {
            const needsDetails =
              receiveEntryInstructionsEmails && !order.hostOrderDetailsInputted;
            return (
              <button
                key={order._id || index}
                onClick={() => navigate(order?._id ? `/order-details/${order._id}` : "/order-list")}
                className={`w-full text-left flex items-center gap-3 p-2.5 rounded-[10px] border transition-colors ${
                  needsDetails
                    ? "bg-[#FEF2F2] border-[#FECACA] hover:bg-[#FEE2E2]"
                    : "bg-[#FAFAFA] border-transparent hover:bg-[#F7F7F8]"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-[8px] flex-shrink-0 flex items-center justify-center ${
                    needsDetails
                      ? "bg-[#EF4949] text-white"
                      : "bg-white border border-[#EAEAEA] text-[#5F6368]"
                  }`}
                >
                  {needsDetails ? <AlertTriangle size={13} /> : <Clock size={13} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-bold text-[#111111] truncate tabular-nums">
                      #
                      {order.uniqueId ||
                        order._id?.slice(-8) ||
                        `ORD-${index + 1}`}
                    </span>
                    {needsDetails && (
                      <span className="px-1.5 py-0.5 rounded-[4px] bg-[#EF4949] text-white text-[8px] font-bold uppercase tracking-[0.1em]">
                        Needs info
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#888888] truncate">
                    {order.property?.listingTitle ||
                      order.property?.address?.city ||
                      "Property"}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p
                    className={`text-[12px] font-extrabold tabular-nums ${
                      needsDetails ? "text-[#EF4949]" : "text-[#111111]"
                    }`}
                  >
                    ${order.hostFee?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-[9px] text-[#888888] mt-0.5">
                    {formatDate(order.inStockDate || order.createdAt)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Quick Actions ───────────────────────────────────────────────── */
function QuickActionsWidget() {
  const navigate = useNavigate();
  const { hostInfo } = useSelector((state) => state.host);

  const actions = [
    {
      title: "Add property",
      description: "List a new rental",
      icon: <Plus size={14} strokeWidth={2.5} />,
      link: "/add-property",
      accent: true,
    },
    {
      title: "View orders",
      description: "Check recent bookings",
      icon: <ShoppingBag size={14} />,
      link: "/order-list",
    },
    {
      title: "Earnings",
      description: "Track performance",
      icon: <DollarSign size={14} />,
      link: "/earnings",
    },
    {
      title: "Settings",
      description: "Account & profile",
      icon: <Settings size={14} />,
      link: `/settings/${hostInfo?._id}`,
    },
  ];

  return (
    <div className="rounded-[18px] bg-white border border-[#EAEAEA] p-5 h-full flex flex-col">
      <div className="mb-4">
        <div className="inline-flex items-center gap-1.5 mb-1">
          <TrendingUp size={12} className="text-[#EF4949]" />
          <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
            Actions
          </span>
        </div>
        <h3
          className="text-[16px] font-extrabold text-[#111111]"
          style={{ letterSpacing: -0.2 }}
        >
          Quick actions
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-2 flex-1">
        {actions.map((action) => (
          <button
            key={action.title}
            onClick={() => navigate(action.link)}
            className="group text-left p-3 rounded-[12px] bg-[#FAFAFA] border border-[#EAEAEA] hover:bg-white hover:border-[#111111] transition-all"
          >
            <div
              className={`w-8 h-8 rounded-[8px] flex items-center justify-center mb-2 transition-colors ${
                action.accent
                  ? "bg-[#EF4949] text-white"
                  : "bg-white border border-[#EAEAEA] text-[#5F6368] group-hover:bg-[#111111] group-hover:text-white group-hover:border-[#111111]"
              }`}
            >
              {action.icon}
            </div>
            <div
              className="text-[12px] font-bold text-[#111111]"
              style={{ letterSpacing: -0.1 }}
            >
              {action.title}
            </div>
            <div className="text-[10px] text-[#888888] mt-0.5 leading-snug">
              {action.description}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */
const HostScreen = () => {
  const dispatch = useDispatch();

  const [, setSearch] = useState("");
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [gateLoading, setGateLoading] = useState(true);
  const [, setOnboardingGate] = useState({
    shouldShowTutorial: false,
    shouldShowOnboardingCall: false,
    isOnBoarded: false,
    activePropertyCount: 0,
    hasCompletedTutorial: false,
    tutorialCompletedAt: null,
  });
  const [allOrders, setAllOrders] = useState([]);

  const { hostInfo } = useSelector((state) => state.host);

  const [, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!hostInfo?._id) return;

    const fetchData = async () => {
      try {
        const response = await request.get(
          `properties/host/${hostInfo._id}/commission`
        );
        setData(response.data.commission);
      } catch (error) {
        console.error("An error occurred:", error);
        setData([]);
      }
    };

    const fetchOrders = async () => {
      try {
        const action = await dispatch(getAllOrders(hostInfo._id));
        const fetchedOrders = action?.payload?.orders;
        setAllOrders(Array.isArray(fetchedOrders) ? fetchedOrders : []);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setAllOrders([]);
      }
    };

    if (hostInfo._id) {
      setIsLoading(true);
      dispatch(getFlags(hostInfo._id));
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      fetchData();
      fetchOrders();
      setIsLoading(false);
    }
  }, [hostInfo?._id, dispatch]);

  useEffect(() => {
    if (!hostInfo?._id) return;

    const fetchOnboardingGate = async () => {
      setGateLoading(true);
      try {
        const { data } = await request.get("/hosts/onboarding-gate", {
          withCredentials: true,
        });
        const gate = data?.gate || {};
        setOnboardingGate({
          shouldShowTutorial: Boolean(gate.shouldShowTutorial),
          shouldShowOnboardingCall: Boolean(gate.shouldShowOnboardingCall),
          isOnBoarded: Boolean(gate.isOnBoarded),
          activePropertyCount: Number(gate.activePropertyCount || 0),
          hasCompletedTutorial: Boolean(gate.hasCompletedTutorial),
          tutorialCompletedAt: gate.tutorialCompletedAt || null,
        });
        setShowTutorial(Boolean(gate.shouldShowTutorial));
      } catch (error) {
        console.error("Error fetching onboarding gate:", error);
        setOnboardingGate({
          shouldShowTutorial: false,
          shouldShowOnboardingCall: false,
          isOnBoarded: false,
          activePropertyCount: 0,
          hasCompletedTutorial: false,
          tutorialCompletedAt: null,
        });
        setShowTutorial(false);
      } finally {
        setGateLoading(false);
      }
    };

    fetchOnboardingGate();
  }, [hostInfo?._id]);

  const { loading: loadingProperties, properties, error } = useSelector(
    (state) => state.hostProperties
  );

  const { receiveEntryInstructionsEmails } = useSelector(
    (state) => state.hostFlags
  );

  const { success: successUpdate, message: messsageupdate } = useSelector(
    (state) => state.propertyUpdate
  );
  const { success: successDelete, message: messageDelete } = useSelector(
    (state) => state.propertyDelete
  );
  const { success: successAddProperty, msg: messageAddProperty } = useSelector(
    (state) => state.addProperty
  );

  useEffect(() => {
    if ((successUpdate || successDelete) && hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
    }
  }, [successUpdate, successDelete, dispatch, hostInfo?._id]);

  useEffect(() => {
    if (successUpdate || successDelete || successAddProperty) {
      setTimeout(() => {
        successUpdate && dispatch(updatePropertyReset());
        successDelete && dispatch(deletePropertyReset());
        successAddProperty && dispatch(addPropertyReset());
      }, 2000);
    }
  }, [successAddProperty, successUpdate, successDelete, dispatch]);

  const handleNext = () => setTutorialStep((prev) => prev + 1);
  const handleBack = () => setTutorialStep((prev) => prev - 1);
  const handleClose = async () => {
    try {
      await request.post(
        "/hosts/onboarding-gate/complete",
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error completing tutorial gate:", error);
    }
    setShowTutorial(false);
    setOnboardingGate((prev) => ({
      ...prev,
      shouldShowTutorial: false,
      shouldShowOnboardingCall: false,
      hasCompletedTutorial: true,
      tutorialCompletedAt: new Date().toISOString(),
    }));
  };

  const activeProperties = Array.isArray(properties)
    ? properties.filter((p) => !p.isDeleted)
    : [];
  const uniqueOrders = Array.from(
    new Map(
      (Array.isArray(allOrders) ? allOrders : []).map((order) => [
        order?._id || order?.uniqueId,
        order,
      ])
    ).values()
  );
  const finalizedOrders = uniqueOrders.filter(
    (order) => !isStatus(order?.status, "created")
  );
  const earnedOrders = finalizedOrders.filter(
    (order) =>
      isStatus(order?.status, "completed") ||
      isStatus(order?.status, "paymentCompleted")
  );
  const totalOrders = finalizedOrders.length;
  const totalEarnings = earnedOrders.reduce(
    (sum, order) => sum + (Number(order?.hostFee) || 0),
    0
  );
  const avgOrderValue =
    earnedOrders.length > 0 ? totalEarnings / earnedOrders.length : 0;

  if (loadingProperties || isLoading || gateLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={44} sx={{ color: "#EF4949" }} />
          <p className="mt-4 text-[12px] font-bold uppercase tracking-[0.22em] text-[#888888]">
            Loading dashboard
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showTutorial && (
        <TutorialModal
          step={tutorialStep}
          onNext={handleNext}
          onBack={handleBack}
          onClose={handleClose}
        />
      )}
      <div
        className="min-h-screen bg-[#FAFAFA]"
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div className="max-w-[1280px] mx-auto px-6 py-6">
          <HostNav searchBar={true} setSearch={setSearch} />

          {/* Flash messages */}
          <div className="space-y-2 mb-3">
            {successAddProperty && (
              <Alert severity="success" sx={{ borderRadius: "12px" }}>
                {messageAddProperty}
              </Alert>
            )}
            {successUpdate && (
              <Alert severity="success" sx={{ borderRadius: "12px" }}>
                {messsageupdate}
              </Alert>
            )}
            {successDelete && (
              <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                {messageDelete}
              </Alert>
            )}
          </div>

          <div className="space-y-5">
            {error && (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                {error}
              </Alert>
            )}

            {/* <ConciergePromoCard hostId={hostInfo._id} /> */}

            <HostAnalyticsPanel hostId={hostInfo._id} />

            <AnalyticsBar
              activeProperties={activeProperties.length}
              totalEarnings={totalEarnings}
              totalOrders={totalOrders}
              avgOrderValue={avgOrderValue}
            />

            <div className="grid grid-cols-1 laptop:grid-cols-2 gap-5 min-h-[380px]">
              <UpcomingOrdersWidget
                orders={allOrders}
                receiveEntryInstructionsEmails={receiveEntryInstructionsEmails}
              />
              <QuickActionsWidget />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HostScreen;
